import React, { useEffect, useState, createContext, useContext, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * BeaconBoostContext — fetches the currently-boosted seller set once
 * per session and exposes a quick `isBoosted(user_id)` lookup. Mount
 * the Provider near the App root; any card/profile can call
 * `useBoosted()` to know whether to show the boost pill.
 */
const BeaconBoostContext = createContext({
  set: new Set(),
  isBoosted: () => false,
  getMeta: () => null,
  refresh: () => {},
  removeLocal: () => {},
});

export function BeaconBoostProvider({ children }) {
  const [items, setItems] = useState([]);

  const load = useCallback(
    () =>
      api
        .get("/beacon/boosted")
        .then((r) => setItems(r.data?.items || []))
        .catch(() => {}),
    []
  );

  useEffect(() => {
    let cancelled = false;
    const run = () => { if (!cancelled) load(); };
    run();
    // Refresh every 5 minutes — boost expiry is on a 24h scale so
    // sub-minute precision isn't required, but visitors who linger
    // shouldn't see ghost pills.
    const t = setInterval(run, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [load]);

  // Optimistically remove a single boosted user from the local cache
  // (used by the admin quick-end-boost toggle so the pill disappears
  // instantly without waiting for the 5min poll).
  const removeLocal = useCallback((userId) => {
    setItems((list) => list.filter((i) => i.user_id !== userId));
  }, []);

  const set = new Set(items.map((i) => i.user_id));
  const meta = Object.fromEntries(items.map((i) => [i.user_id, i]));

  return (
    <BeaconBoostContext.Provider
      value={{
        set,
        isBoosted: (id) => set.has(id),
        getMeta: (id) => meta[id] || null,
        refresh: load,
        removeLocal,
      }}
    >
      {children}
    </BeaconBoostContext.Provider>
  );
}

export function useBoosted() {
  return useContext(BeaconBoostContext);
}

/**
 * BeaconBoostPill — amber pill rendered on listings/seller cards while
 * the seller has an active Beacon Boost. Self-hides if the seller is
 * not boosted, so it's drop-in safe.
 *
 * Admin behavior: when the current user is an admin, the pill becomes
 * a button that ends the boost instantly (confirm-then-delete), mirroring
 * the `AdminBeaconQuickToggle` UX for the Spotlight toggle. Non-admins
 * see the static pill.
 */
export default function BeaconBoostPill({ userId, size = "sm" }) {
  const { isBoosted, getMeta, removeLocal } = useBoosted();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!userId || !isBoosted(userId)) return null;
  const meta = getMeta(userId);
  const isAdmin = user?.role === "admin";
  const cls =
    size === "lg"
      ? "px-3 py-1 text-xs"
      : "px-2 py-0.5 text-[10px]";

  // Non-admin: static badge.
  if (!isAdmin) {
    return (
      <span
        title={
          meta?.beacon_boost_until
            ? `Beacon Boost · until ${new Date(meta.beacon_boost_until).toLocaleString()}`
            : "Beacon Boost"
        }
        data-testid={`beacon-boost-pill-${userId}`}
        className={`inline-flex items-center gap-1 rounded-full bg-amber-300 text-amber-950 font-black uppercase tracking-widest ${cls}`}
      >
        🌀 Boost
      </span>
    );
  }

  // Admin: click-to-end-boost button (mirrors the Spotlight toggle UX).
  const endBoost = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    if (!window.confirm("End this seller's Beacon Boost now?")) return;
    setBusy(true);
    removeLocal(userId); // optimistic hide
    try {
      await api.delete(`/admin/beacon/boost/${userId}`);
      toast.success("Boost ended");
    } catch (err) {
      toast.error(apiError(err));
      // The next 5-min refresh will repaint the pill if the server
      // still considers the boost active.
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={endBoost}
      disabled={busy}
      title="ADMIN: Click to end this seller's Beacon Boost"
      data-testid={`beacon-boost-pill-${userId}`}
      className={`inline-flex items-center gap-1 rounded-full bg-amber-300 hover:bg-amber-200 text-amber-950 font-black uppercase tracking-widest border-2 border-amber-400 transition ${cls} ${busy ? "opacity-60 cursor-wait" : "cursor-pointer"}`}
    >
      🌀 Boost
      <span className="ml-1 text-[8px] opacity-70">ADMIN</span>
    </button>
  );
}
